import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FormShell, FormField, FormFooter } from '../../../../../../generic/ui/FormLayout';
import { AccentButton } from '../../../../../../generic/ui/Button';
import ProductSelector from '../../../../../../generic/ui/Selectors/ProductSelector';
import SizeSelector from '../../../../../../generic/ui/Selectors/SizeSelector';
import PackageSelector from '../../../../../../generic/ui/Selectors/PackageSelector';
import { Defaults } from '../../../../../../modules/API/Defaults/defaults';
import { Product } from '../../../../../../modules/API/Products/products';
import { IProductsWithVarietyItem } from '../../../../../../modules/API/Defaults/models/compliteProducts';
import { ISizeItem } from '../../../../../../modules/API/Products/models/size';
import { IPackagesItem } from '../../../../../../modules/API/Defaults/models/packages';
import useDataFetcher from '../../../knisot/hooks/useFetchData';
import { IManualProductSelection } from '../helpers/manualProductEntry';

interface Props {
    packageList: IPackagesItem[];
    onSubmit: (selection: IManualProductSelection) => void;
    closeFunc?: (flag?: boolean) => void;
}

const ModalManualProduct: React.FC<Props> = ({ packageList, onSubmit }) => {
    const [t] = useTranslation(['likut', 'buttons']);
    const [product, setProduct] = useState<IProductsWithVarietyItem | null>(null);
    const [size, setSize] = useState<ISizeItem | null>(null);
    const [pack, setPack] = useState<IPackagesItem | null>(null);

    const { data: products } = useDataFetcher<IProductsWithVarietyItem[]>(
        Defaults.getProductsWithVariety, 'data'
    );

    const { data: sizeListByProduct } = useDataFetcher<ISizeItem[]>(
        Product.getSizesByProduct, product ? product.ProductID.toString() : ''
    );

    const handleProductSelect = (item: IProductsWithVarietyItem | null) => {
        setProduct(item);
        setSize(null);
    };

    const showSize = !!product && (sizeListByProduct?.length ?? 0) > 0;
    const isActiveSave = !!product && !!pack;

    const handleSave = () => {
        if (!product || !pack) return;
        onSubmit({ product, size, pack });
    };

    return (
        <FormShell>
            <PairRow>
                <FormField label={t('likut_modal_manual_product_product_label')} required>
                    <ProductSelector
                        products={products ?? []}
                        value={product}
                        onSelect={handleProductSelect}
                    />
                </FormField>
                {
                    showSize && (
                        <FormField label={t('likut_modal_manual_product_size_label')}>
                            <SizeSelector
                                sizes={sizeListByProduct ?? []}
                                value={size}
                                onSelect={setSize}
                            />
                        </FormField>
                    )
                }
            </PairRow>
            <FormField label={t('likut_modal_manual_product_pack_label')} required fullWidth>
                <PackageSelector
                    packages={packageList ?? []}
                    value={pack}
                    onSelect={setPack}
                />
            </FormField>
            <FormFooter>
                <AccentButton
                    type='button'
                    onClick={handleSave}
                    disabled={!isActiveSave}
                    fullWidth={false}
                >
                    {t('save', { ns: 'buttons' })}
                </AccentButton>
            </FormFooter>
        </FormShell>
    );
};

export default ModalManualProduct;

const PairRow = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 0.5rem;

    > :only-child {
        grid-column: 1 / -1;
    }
`;
